import { bffRequest } from '../../../common/services/httpClient';
import { FSS_API_PATHS } from '../../../constants/apiPaths';
import {
  BULK_UPLOAD_POLL_INTERVAL_MS,
  BULK_UPLOAD_POLL_MAX_ATTEMPTS,
} from '../../../constants/timing';
import { getShellState } from '../../../shell/store/shellStore';
import { extractCanvasUkeyFromPath, resolveUploadSubpath, resolveUploadTenantUkey, UPLOAD_BUCKET_UKEY_FOR_UPLOAD } from '../constants/fileUploadConstants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** GSF OperationResponse envelope returned by every FSS endpoint. */
interface FssEnvelope<T> {
  status: { status_code: number; details: Record<string, unknown> };
  response: T;
}

/** Shape returned by POST /upload/file */
export interface UploadFileResponse {
  stored_file_ukey: string;
  file_name: string;
  status: string;
  bytes_size: number;
  destination_path?: string;
  file_hash?: string;
  tracker_id?: string;
  source_type?: string;
  agent_run_id?: string | null;
  execution_id?: string | null;
}

/** 202 accepted response from POST /upload/bulk — job submitted asynchronously. */
interface BulkJobInitResponse {
  job_id: string;
  status: string;
  total_files: number;
  message: string;
  agent_run_id: string | null;
  execution_id: string | null;
}

/** Job metadata object nested inside GET /upload/jobs/{job_id} response. */
interface BulkJobInfo {
  job_id: string;
  /** PENDING | PROCESSING | COMPLETED | FAILED */
  status: string;
  total_files: number;
  processed_files: number;
  failed_files: number;
  start_time: string | null;
  end_time: string | null;
  last_heartbeat_at: string | null;
  error_message: string | null;
  agent_run_id: string | null;
  execution_id: string | null;
}

/** Per-file result returned inside GET /upload/jobs/{job_id} response. */
interface BulkJobFileResult {
  file_id: string;
  file_name: string;
  status: string;
  content_type: string;
  file_size: number;
  tracker_id: string | null;
  error_message: string | null;
}

/** Full response body from GET /upload/jobs/{job_id}. */
export interface BulkJobStatusResponse {
  job: BulkJobInfo;
  files: BulkJobFileResult[];
}

// ---------------------------------------------------------------------------
// Header injection (DR-4)
// ---------------------------------------------------------------------------

/**
 * Resolve the active canvas/module ukey and return { 'X-Canvas-Ukey': ukey } or {}.
 * Manual injection required because the ESA Panel is host-level code outside
 * CanvasContextProvider — SurfaceBffClient does not auto-inject this header.
 *
 * A plain module load populates activeModule (not activeCanvas), so fall back
 * through activeCanvas → activeModule → activeSolution to find the ukey.
 */
export function buildCanvasHeaders(): Record<string, string> {
  const canvasUkey = resolveActiveSurfaceUkey();
  return canvasUkey ? { 'X-Canvas-Ukey': canvasUkey } : {};
}

function resolveActiveSurfaceUkey(): string | null {
  const state = getShellState() as {
    activeCanvas?: string | null;
    activeModule?: string | null;
    activeSolution?: string | null;
  };
  const urlCanvas =
    typeof window !== 'undefined' ? extractCanvasUkeyFromPath(window.location.pathname) : null;
  return state.activeCanvas ?? state.activeModule ?? state.activeSolution ?? urlCanvas ?? null;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * POST /services/files/upload/file
 * Upload a single file as multipart/form-data and receive the server-assigned metadata.
 * Injects X-Canvas-Ukey via buildCanvasHeaders so the ESA FileTypeGuard can enforce
 * canvas-specific type rules.
 */
export async function uploadFile(file: File): Promise<UploadFileResponse> {
  const form = new FormData();
  form.append('file', file);
  const uploadSubpath = resolveUploadSubpath({
    fileName: file.name,
    surfaceUkey: resolveActiveSurfaceUkey(),
  });
  if (uploadSubpath) {
    form.append('upload_subpath', uploadSubpath);
  }
  form.append('tenant_ukey', resolveUploadTenantUkey());
  form.append('bucket_ukey', UPLOAD_BUCKET_UKEY_FOR_UPLOAD);
  const raw = await bffRequest<FssEnvelope<UploadFileResponse>>(FSS_API_PATHS.UPLOAD_FILE, {
    method: 'POST',
    body: form,
    headers: buildCanvasHeaders(),
  });
  return raw.response;
}

/**
 * GET /services/files/upload/jobs/{jobId}
 * Poll the async bulk-upload job until it reaches COMPLETED or FAILED.
 * Returns the ordered file results on success; throws on failure or timeout.
 *
 * `intervalMs` defaults to 2 s in production; pass 0 in tests that cover the
 * PENDING → COMPLETED transition to avoid real timer waits.
 */
export async function pollBulkJobUntilDone(
  jobId: string,
  options?: { intervalMs?: number; maxAttempts?: number },
): Promise<UploadFileResponse[]> {
  const intervalMs = options?.intervalMs ?? BULK_UPLOAD_POLL_INTERVAL_MS;
  const maxAttempts = options?.maxAttempts ?? BULK_UPLOAD_POLL_MAX_ATTEMPTS;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const raw = await bffRequest<FssEnvelope<BulkJobStatusResponse>>(
      `${FSS_API_PATHS.uploadJob(jobId)}?tenant_ukey=${encodeURIComponent(resolveUploadTenantUkey())}`,
      { method: 'GET' },
    );
    const { job, files } = raw.response;

    if (job.status === 'COMPLETED') {
      // Map BulkJobFileResult → UploadFileResponse so callers use a uniform shape.
      return files.map(f => ({
        stored_file_ukey: f.file_id,
        file_name: f.file_name,
        status: f.status,
        bytes_size: f.file_size,
        tracker_id: f.tracker_id ?? undefined,
      }));
    }

    if (job.status === 'FAILED') {
      throw new Error(`Bulk upload job ${jobId} failed: ${job.error_message ?? 'unknown error'}`);
    }

    // PENDING or PROCESSING — wait before the next attempt (skip delay on last)
    if (attempt < maxAttempts - 1) {
      await new Promise<void>(resolve => setTimeout(resolve, intervalMs));
    }
  }

  throw new Error(`Bulk upload job ${jobId} timed out`);
}

/**
 * POST /services/files/upload/bulk
 * Submit a multi-file upload and wait for the async job to complete.
 * Returns results in the same order as the input files array.
 */
export async function uploadFileBulk(files: File[]): Promise<UploadFileResponse[]> {
  const form = new FormData();
  for (const file of files) {
    form.append('files', file);
  }
  const uploadSubpath = resolveUploadSubpath({
    fileName: files[0]?.name,
    surfaceUkey: resolveActiveSurfaceUkey(),
  });
  if (uploadSubpath) {
    form.append('upload_subpath', uploadSubpath);
  }
  form.append('tenant_ukey', resolveUploadTenantUkey());
  form.append('bucket_ukey', UPLOAD_BUCKET_UKEY_FOR_UPLOAD);

  const raw = await bffRequest<FssEnvelope<BulkJobInitResponse>>(FSS_API_PATHS.UPLOAD_BULK, {
    method: 'POST',
    body: form,
    headers: buildCanvasHeaders(),
  });

  return pollBulkJobUntilDone(raw.response.job_id);
}

/**
 * DELETE /services/files/stored-files/{ukey}
 * Soft-delete file metadata when user removes an attachment chip.
 * FSS marks the row inactive; GCS object cleanup is deferred to a background TTL job.
 */
export async function deleteStoredFile(ukey: string): Promise<void> {
  await bffRequest(FSS_API_PATHS.storedFile(ukey), {
    method: 'DELETE',
  });
}

/**
 * GET /services/files/stored-files/{ukey}/download?tenant_ukey={tenant}
 * Fetches the file as a blob (auth headers injected by BFF adapter) and
 * triggers a browser download via a temporary object URL.
 */
export async function downloadStoredFile(ukey: string, fileName: string): Promise<void> {
  const qs = `?tenant_ukey=${encodeURIComponent(resolveUploadTenantUkey())}`;
  const blob = await bffRequest<Blob>(
    `${FSS_API_PATHS.storedFileDownload(ukey)}${qs}`,
    { method: 'GET', responseType: 'blob' },
  );

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName || ukey;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}
